import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { ContentHeader } from "@components";
import API from "../api";
import toastr from "toastr";

// ─── Types ────────────────────────────────────────────────────────────────────

// Result-alert style: which of the 4 visual treatments is applied to
// High/Low flagged results on the printed report.
type ResultAlertType = "bold" | "color" | "text" | "arrow";

interface OrgSettings {
  enable_report_header?: boolean;
  lab_name?: string;
  location?: string;
  contact_no?: string;
  enable_receipt_header?: boolean;
  patient_code_prefix?: string;
  logo?: string | File;
  header_logo_1?: string | File;
  header_logo_2?: string | File;
  report_footer?: string;

  // ── Result Alert ──────────────────────────────────────────────────
  enable_result_alert?: boolean;
  result_alert_type?: ResultAlertType;
  result_alert_color?: string; // used only when result_alert_type === "color"
}

type LogoField = "logo" | "header_logo_1" | "header_logo_2";

interface CropState {
  src: string | null;
  crop: Crop;
  completedCrop: PixelCrop | null;
  open: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeCenterCrop(mediaWidth: number, mediaHeight: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, 16 / 9, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

// Always output PNG to avoid mime/extension mismatch from canvas
async function getCroppedBlob(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  fileName: string
): Promise<File> {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth  / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width  = Math.floor(pixelCrop.width  * scaleX);
  canvas.height = Math.floor(pixelCrop.height * scaleY);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    Math.floor(pixelCrop.x      * scaleX),
    Math.floor(pixelCrop.y      * scaleY),
    Math.floor(pixelCrop.width  * scaleX),
    Math.floor(pixelCrop.height * scaleY),
    0, 0,
    Math.floor(pixelCrop.width  * scaleX),
    Math.floor(pixelCrop.height * scaleY)
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Canvas toBlob failed"));
        const baseName = fileName.replace(/\.[^.]+$/, "");
        resolve(new File([blob], `${baseName}.png`, { type: "image/png" }));
      },
      "image/png",
      1
    );
  });
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const theme = {
  page: {
    minHeight: "100vh",
    color: "#e2e8f0",
    fontFamily: "'Segoe UI', sans-serif",
    padding: "20px",
  } as React.CSSProperties,
  wrap: { maxWidth: 900, margin: "0 auto" },
  card: {
    borderRadius: 8,
    padding: "24px 28px",
    marginBottom: 20,
    border: "1px solid #374151",
    background: "rgba(255,255,255,0.07)",
  },
  sectionHead: {
    fontSize: 18,
    fontWeight: 700,
    color: "#f1f5f9",
    margin: "0 0 20px",
    textAlign: "center" as const,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: 16,
    alignItems: "start",
    marginBottom: 14,
  } as React.CSSProperties,
  label: { color: "#94a3b8", fontSize: 14, fontWeight: 500, paddingTop: 10 },
  input: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 6,
    border: "1px solid #4b5563",
    background: "rgba(255,255,255,0.08)",
    color: "#f1f5f9",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box" as const,
    fontFamily: "inherit",
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 6,
    border: "1px solid #4b5563",
    background: "rgba(255,255,255,0.08)",
    color: "#f1f5f9",
    fontSize: 14,
    outline: "none",
    resize: "vertical" as const,
    minHeight: 100,
    boxSizing: "border-box" as const,
    fontFamily: "inherit",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    color: "#94a3b8",
    fontSize: 14,
  } as React.CSSProperties,
  btnUpdate: {
    padding: "10px 40px",
    backgroundColor: "#06b6d4",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    display: "block",
    margin: "20px auto 0",
  } as React.CSSProperties,

  modalOverlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalBox: {
    background: "#1e2533",
    borderRadius: 10,
    padding: 24,
    maxWidth: "92vw",
    maxHeight: "92vh",
    overflow: "auto",
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
  },
  modalTitle: {
    color: "#f1f5f9",
    fontSize: 16,
    fontWeight: 700,
    margin: 0,
    borderBottom: "1px solid #374151",
    paddingBottom: 12,
  },
  modalHint: { color: "#64748b", fontSize: 12, margin: "-8px 0 0" },
  modalBtns: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    borderTop: "1px solid #374151",
    paddingTop: 12,
  },
  btnApply: {
    padding: "9px 32px",
    backgroundColor: "#06b6d4",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  btnCancel: {
    padding: "9px 22px",
    backgroundColor: "transparent",
    color: "#94a3b8",
    border: "1px solid #4b5563",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
  },
  chooseBtn: {
    padding: "7px 18px",
    backgroundColor: "#334155",
    color: "#cbd5e1",
    border: "1px solid #4b5563",
    borderRadius: 5,
    fontSize: 13,
    cursor: "pointer",
    marginBottom: 8,
    display: "inline-block" as const,
  },
  reCropBtn: {
    padding: "5px 14px",
    backgroundColor: "#334155",
    color: "#94a3b8",
    border: "1px solid #4b5563",
    borderRadius: 5,
    fontSize: 12,
    cursor: "pointer",
    marginTop: 6,
    display: "inline-block" as const,
  },
  previewImg: {
    maxWidth: "100%",
    borderRadius: 6,
    border: "1px solid #4b5563",
    display: "block" as const,
    marginBottom: 4,
  },

  // ── Result Alert section ──────────────────────────────────────────
  alertOptionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
  } as React.CSSProperties,
  alertOptionCard: (active: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "14px 10px",
    borderRadius: 8,
    cursor: "pointer",
    border: active ? "1.5px solid #06b6d4" : "1px solid #4b5563",
    background: active ? "rgba(6,182,212,0.12)" : "rgba(255,255,255,0.05)",
    color: active ? "#e0f7fa" : "#cbd5e1",
    fontSize: 13,
    fontWeight: 600,
    transition: "all .15s ease",
  }),
  alertPreviewBadge: {
    fontSize: 18,
    lineHeight: 1,
  } as React.CSSProperties,
  colorPickerRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
  } as React.CSSProperties,
  colorSwatchInput: {
    width: 44,
    height: 34,
    padding: 2,
    borderRadius: 6,
    border: "1px solid #4b5563",
    background: "transparent",
    cursor: "pointer",
  } as React.CSSProperties,
};

// ─── Individual Logo Upload + Crop ────────────────────────────────────────────

interface LogoFieldProps {
  label: string;
  field: LogoField;
  preview: string | null;
  onCroppedFile: (field: LogoField, file: File, previewUrl: string) => void;
  maxHeight?: number;
}

function LogoUploadField({ label, field, preview, onCroppedFile, maxHeight = 130 }: LogoFieldProps) {
  const [cropState, setCropState] = useState<CropState>({
    src: null,
    crop: { unit: "%", x: 5, y: 5, width: 90, height: 90 },
    completedCrop: null,
    open: false,
  });
  const [rawFileName, setRawFileName] = useState("image.png");
  const [applying, setApplying]       = useState(false);

  const imgRef       = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => fileInputRef.current?.click();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (cropState.src) URL.revokeObjectURL(cropState.src);
    setRawFileName(file.name);
    setCropState({
      src: URL.createObjectURL(file),
      crop: { unit: "%", x: 5, y: 5, width: 90, height: 90 },
      completedCrop: null,
      open: true,
    });
    e.target.value = "";
  };

  // KEY FIX #1: Set completedCrop immediately when image loads
  // so Apply works even if the user never drags the crop handles
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const { width, height } = img;
    const centered  = makeCenterCrop(width, height);
    const pixelCrop = convertToPixelCrop(centered, width, height);
    setCropState((prev) => ({
      ...prev,
      crop: centered,
      completedCrop: pixelCrop, // ← always have a valid completedCrop
    }));
  }, []);

  // KEY FIX #2: Use imgRef.current directly — ref is on the <img> inside ReactCrop
  const handleApply = useCallback(async () => {
    const img = imgRef.current;
    if (!img) {
      toastr.error("Image not ready. Please wait and try again.");
      return;
    }
    if (!cropState.completedCrop || cropState.completedCrop.width === 0) {
      toastr.error("Please select a crop area.");
      return;
    }
    setApplying(true);
    try {
      const croppedFile = await getCroppedBlob(img, cropState.completedCrop, rawFileName);
      const previewUrl  = URL.createObjectURL(croppedFile);
      onCroppedFile(field, croppedFile, previewUrl);
      setCropState((prev) => ({ ...prev, open: false }));
    } catch (err) {
      console.error("Crop failed:", err);
      toastr.error("Failed to crop image. Please try again.");
    } finally {
      setApplying(false);
    }
  }, [cropState.completedCrop, field, onCroppedFile, rawFileName]);

  const handleCancel = () => {
    if (cropState.src) URL.revokeObjectURL(cropState.src);
    setCropState({ src: null, crop: { unit: "%", x: 5, y: 5, width: 90, height: 90 }, completedCrop: null, open: false });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        style={{ display: "none" }}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
      />

      <div style={theme.row}>
        <label style={theme.label}>{label}</label>
        <div>
          {preview ? (
            <>
              <img src={preview} alt={label} style={{ ...theme.previewImg, maxHeight }} />
              <button type="button" style={theme.reCropBtn} onClick={openPicker}>
                ✎ Change / Re-crop
              </button>
            </>
          ) : (
            <button type="button" style={theme.chooseBtn} onClick={openPicker}>
              📁 Choose &amp; Crop Image
            </button>
          )}
        </div>
      </div>

      {/* Crop Modal */}
      {cropState.open && cropState.src && (
        <div style={theme.modalOverlay}>
          <div style={theme.modalBox}>
            <p style={theme.modalTitle}>Crop Image — {label}</p>
            <p style={theme.modalHint}>
              Drag handles to resize · Drag inside to reposition · Click Apply Crop when ready
            </p>

            {/*
              KEY FIX #3: imgRef goes on the <img> that is a DIRECT child of ReactCrop.
              Never wrap it in an extra div. This is what makes getCroppedBlob work.
            */}
            <ReactCrop
              crop={cropState.crop}
              onChange={(c) => setCropState((prev) => ({ ...prev, crop: c }))}
              onComplete={(c) => setCropState((prev) => ({ ...prev, completedCrop: c }))}
              keepSelection
              minWidth={10}
              minHeight={10}
            >
              <img
                ref={imgRef}
                src={cropState.src}
                alt="Crop source"
                onLoad={onImageLoad}
                crossOrigin="anonymous"
                style={{ maxWidth: "72vw", maxHeight: "60vh", display: "block", objectFit: "contain" }}
              />
            </ReactCrop>

            <div style={theme.modalBtns}>
              <button type="button" style={theme.btnCancel} onClick={handleCancel} disabled={applying}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...theme.btnApply, opacity: applying ? 0.7 : 1, cursor: applying ? "wait" : "pointer" }}
                onClick={handleApply}
                disabled={applying}
              >
                {applying ? "Applying…" : "✔ Apply Crop"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Result Alert option definitions ──────────────────────────────────────────

const RESULT_ALERT_OPTIONS: { value: ResultAlertType; label: string; preview: React.ReactNode; hint: string }[] = [
  {
    value: "bold",
    label: "Bold",
    preview: <span style={{ fontWeight: 800 }}>120</span>,
    hint: "High/Low results are shown in bold text",
  },
  {
    value: "color",
    label: "Color",
    preview: <span style={{ fontWeight: 700 }}>120</span>,
    hint: "High/Low results are shown in the color you pick below",
  },
  {
    value: "text",
    label: "Text (H/L)",
    preview: (
      <span>
        120<sup style={{ fontWeight: 800 }}>H</sup>
      </span>
    ),
    hint: '"H" for high results, "L" for low results',
  },
  {
    value: "arrow",
    label: "Arrow",
    preview: <span>120 ▲</span>,
    hint: "Up arrow (▲) for high, down arrow (▼) for low",
  },
];

const DEFAULT_ALERT_COLOR = "#d00000";

// ─── Main Settings Page ───────────────────────────────────────────────────────

export default function Settings() {
  const [settings,     setSettings]     = useState<OrgSettings>({});
  const [saving,       setSaving]       = useState(false);
  const [logoPreview,  setLogoPreview]  = useState<string | null>(null);
  const [logo1Preview, setLogo1Preview] = useState<string | null>(null);
  const [logo2Preview, setLogo2Preview] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      const res  = await API.get("/users/getUserSettings");
      const data = res.data?.data || {};
      setSettings({
        ...data,
        // Normalize the DB's 0/1 (or "0"/"1") into a real boolean for the checkbox.
        enable_result_alert:
          data.enable_result_alert === 1 ||
          data.enable_result_alert === "1" ||
          data.enable_result_alert === true,
        result_alert_type: data.result_alert_type || "text",
        result_alert_color: data.result_alert_color || DEFAULT_ALERT_COLOR,
      });
      const rawBase = import.meta.env.VITE_API_BASE_URL || API.defaults.baseURL || "";
      const base = rawBase.replace(/\/+$/, "").replace(/\/api$/, "");
      const formatAsset = (pathStr?: string) => {
        if (!pathStr) return null;
        const s = String(pathStr).trim();
        if (!s) return null;
        if (/^https?:\/\//i.test(s)) return s;
        const norm = s.startsWith("/") ? s : (s.startsWith("uploads/") || s.startsWith("logo/") ? `/${s}` : `/uploads/settings/${s}`);
        return `${base}${norm}`;
      };
      if (data.logo)          setLogoPreview(formatAsset(data.logo));
      if (data.header_logo_1) setLogo1Preview(formatAsset(data.header_logo_1));
      if (data.header_logo_2) setLogo2Preview(formatAsset(data.header_logo_2));
    } catch {
      toastr.error("Failed to load settings");
    }
  };

  const handleChange = (field: keyof OrgSettings, value: any) =>
    setSettings((prev) => ({ ...prev, [field]: value }));

  const handleCroppedFile = useCallback(
    (field: LogoField, file: File, previewUrl: string) => {
      setSettings((prev) => ({ ...prev, [field]: file }));
      if (field === "logo")               setLogoPreview(previewUrl);
      else if (field === "header_logo_1") setLogo1Preview(previewUrl);
      else if (field === "header_logo_2") setLogo2Preview(previewUrl);
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("lab_name",            settings.lab_name            || "");
      fd.append("location",            settings.location            || "");
      fd.append("contact_no",          settings.contact_no          || "");
      fd.append("patient_code_prefix", settings.patient_code_prefix || "");
      fd.append("report_footer",       settings.report_footer       || "");
      fd.append("enable_report_header",  settings.enable_report_header  ? "1" : "0");
      fd.append("enable_receipt_header", settings.enable_receipt_header ? "1" : "0");

      // ── Result Alert fields ──────────────────────────────────────
      fd.append("enable_result_alert", settings.enable_result_alert ? "1" : "0");
      fd.append("result_alert_type",   settings.result_alert_type   || "text");
      fd.append("result_alert_color",  settings.result_alert_color  || DEFAULT_ALERT_COLOR);

      if (settings.logo          instanceof File) fd.append("logo",          settings.logo);
      if (settings.header_logo_1 instanceof File) fd.append("header_logo_1", settings.header_logo_1);
      if (settings.header_logo_2 instanceof File) fd.append("header_logo_2", settings.header_logo_2);

      const res = await API.post("/users/updateUserSettings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        toastr.success("Organisation information updated successfully");
        loadSettings();
      } else {
        toastr.error(res.data?.message || "Failed to update");
      }
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const [showImportDefaultButton, setShowImportDefaultButton] = useState(false);
  const [checkingImportData, setCheckingImportData] = useState(true);

  const checkLabDataStatus = async () => {
    try {
      setCheckingImportData(true);

      const checks = await Promise.all([
        API.get("/departments/getDepartments", { params: { page: 1, pageSize: 1 } }),
        API.get("/testcategories/getCategories", { params: { page: 1, pageSize: 1 } }),
        API.get("/testTypes/getTestTypes", { params: { page: 1, pageSize: 1 } }),
        API.get("/groupProfiles/getGroupProfiles", { params: { page: 1, pageSize: 1 } }),
      ]);

      const hasExistingLabData = checks.some((res) => {
        const total = Number(res?.data?.total ?? res?.data?.data?.length ?? 0);
        return total > 0;
      });

      setShowImportDefaultButton(!hasExistingLabData);
    } catch (error) {
      console.error("checkLabDataStatus failed:", error);
      setShowImportDefaultButton(true);
    } finally {
      setCheckingImportData(false);
    }
  };

  const importDefaultLabData = async () => {
    const routes = [
      { label: "Departments", path: "/departments/importDefaultDepartments" },
      { label: "Test Categories", path: "/testcategories/importDefaultCategories" },
      { label: "Test Types", path: "/testTypes/importDefaultTestData" },
      { label: "Group Profiles", path: "/groupProfiles/importDefaultGroupProfiles" },
    ];

    try {
      const imported: string[] = [];

      for (const route of routes) {
        try {
          const res = await API.post(route.path);
          if (res?.data?.success) {
            imported.push(route.label);
          }
        } catch (routeErr: any) {
          console.error(`Import failed for ${route.label}:`, routeErr);
        }
      }

      if (imported.length > 0) {
        toastr.success(`Imported default data for: ${imported.join(", ")}`);
      } else {
        toastr.info("This lab already has default data for all master tables.");
      }

      await checkLabDataStatus();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to import default lab data");
    }
  };

  useEffect(() => {
    loadSettings();
    checkLabDataStatus();
  }, []);

  const alertEnabled = !!settings.enable_result_alert;
  const activeAlertType: ResultAlertType = settings.result_alert_type || "text";

  return (
    <div style={theme.page}>
      <ContentHeader title="Organisation Information" />

      <div style={theme.wrap}>
        {!checkingImportData && showImportDefaultButton && (
          <div style={{ ...theme.card, padding: "18px 20px" }}>
            <h2 style={theme.sectionHead}>Import Default Lab Data</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
              <button type="button" className="btn btn-light" onClick={importDefaultLabData}>
                <i className="fa fa-upload me-1" /> Import Default Data for All Tables
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── Organisation Info Card ──────────────────────────────── */}
          <div style={theme.card}>
            <h2 style={theme.sectionHead}>Organisation Information</h2>

            <div style={theme.checkboxRow}>
              <input
                type="checkbox" id="enable_report_header"
                checked={!!settings.enable_report_header}
                onChange={(e) => handleChange("enable_report_header", e.target.checked)}
              />
              <label htmlFor="enable_report_header">Enable Report Header</label>
            </div>

            <div style={theme.row}>
              <label style={theme.label}>Lab Name</label>
              <input style={theme.input} type="text" value={settings.lab_name || ""}
                onChange={(e) => handleChange("lab_name", e.target.value)} placeholder="Enter lab name" />
            </div>

            <div style={theme.row}>
              <label style={theme.label}>Location</label>
              <input style={theme.input} type="text" value={settings.location || ""}
                onChange={(e) => handleChange("location", e.target.value)} placeholder="Enter location" />
            </div>

            <div style={theme.row}>
              <label style={theme.label}>Phone</label>
              <input style={theme.input} type="text" value={settings.contact_no || ""}
                onChange={(e) => handleChange("contact_no", e.target.value)} placeholder="Enter phone number" />
            </div>

            <div style={theme.checkboxRow}>
              <input
                type="checkbox" id="enable_receipt_header"
                checked={!!settings.enable_receipt_header}
                onChange={(e) => handleChange("enable_receipt_header", e.target.checked)}
              />
              <label htmlFor="enable_receipt_header">Enable Receipt Header</label>
            </div>

            <div style={theme.row}>
              <label style={theme.label}>Patient Code Prefix</label>
              <input style={theme.input} type="text" value={settings.patient_code_prefix || ""}
                onChange={(e) => handleChange("patient_code_prefix", e.target.value)} placeholder="e.g. HCDC" />
            </div>

            {/* Logo with individual crop */}
            <LogoUploadField
              label="Logo"
              field="logo"
              preview={logoPreview}
              onCroppedFile={handleCroppedFile}
              maxHeight={120}
            />
          </div>

          {/* ── Header Logos Card ───────────────────────────────────── */}
          <div style={theme.card}>
            <h2 style={theme.sectionHead}>Header Logos</h2>

            <LogoUploadField
              label="Header Logo 1"
              field="header_logo_1"
              preview={logo1Preview}
              onCroppedFile={handleCroppedFile}
              maxHeight={150}
            />

            <LogoUploadField
              label="Header Logo 2"
              field="header_logo_2"
              preview={logo2Preview}
              onCroppedFile={handleCroppedFile}
              maxHeight={150}
            />
          </div>

          {/* ── Result Alert Card ───────────────────────────────────── */}
          <div style={theme.card}>
            <h2 style={theme.sectionHead}>Result Alert</h2>

            <div style={theme.checkboxRow}>
              <input
                type="checkbox"
                id="enable_result_alert"
                checked={alertEnabled}
                onChange={(e) => handleChange("enable_result_alert", e.target.checked)}
              />
              <label htmlFor="enable_result_alert">
                Enable Result Alert (highlight abnormal High/Low results on the report)
              </label>
            </div>

            {alertEnabled && (
              <>
                <div style={theme.row}>
                  <label style={theme.label}>Alert Style</label>
                  <div style={theme.alertOptionsGrid}>
                    {RESULT_ALERT_OPTIONS.map((opt) => {
                      const active = activeAlertType === opt.value;
                      return (
                        <div
                          key={opt.value}
                          role="button"
                          tabIndex={0}
                          style={theme.alertOptionCard(active)}
                          onClick={() => handleChange("result_alert_type", opt.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") handleChange("result_alert_type", opt.value);
                          }}
                        >
                          <span style={theme.alertPreviewBadge}>{opt.preview}</span>
                          <span>
                            <input
                              type="radio"
                              name="result_alert_type"
                              checked={active}
                              readOnly
                              style={{ marginRight: 6 }}
                            />
                            {opt.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <p style={{ ...theme.modalHint, margin: "0 0 4px" }}>
                  {RESULT_ALERT_OPTIONS.find((o) => o.value === activeAlertType)?.hint}
                </p>

                {activeAlertType === "color" && (
                  <div style={theme.row}>
                    <label style={theme.label}>Alert Color</label>
                    <div style={theme.colorPickerRow}>
                      <input
                        type="color"
                        style={theme.colorSwatchInput}
                        value={settings.result_alert_color || DEFAULT_ALERT_COLOR}
                        onChange={(e) => handleChange("result_alert_color", e.target.value)}
                      />
                      <input
                        style={{ ...theme.input, width: 140 }}
                        type="text"
                        value={settings.result_alert_color || DEFAULT_ALERT_COLOR}
                        onChange={(e) => handleChange("result_alert_color", e.target.value)}
                        placeholder="#d00000"
                      />
                      <span
                        style={{
                          fontWeight: 700,
                          color: settings.result_alert_color || DEFAULT_ALERT_COLOR,
                        }}
                      >
                        Sample: 120
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Report Footer Card ──────────────────────────────────── */}
          <div style={theme.card}>
            <h2 style={theme.sectionHead}>Report Footer</h2>

            <div style={theme.row}>
              <label style={theme.label}>Report Footer</label>
              <textarea
                style={theme.textarea} rows={3}
                value={settings.report_footer || ""}
                onChange={(e) => handleChange("report_footer", e.target.value)}
                placeholder="<h6>Wish you a Speedy Recovery, Thank You!</h6>"
              />
            </div>
          </div>

          <button type="submit" style={theme.btnUpdate} disabled={saving}>
            {saving ? "Updating…" : "Update"}
          </button>
        </form>
      </div>
    </div>
  );
}